import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Crown, CreditCard, Settings, LogOut, ArrowUpRight, User, Clock, Layers, LogIn, UserPlus, Menu, X } from 'lucide-react';
import SubscriptionModal from './subscription/SubscriptionModal';

const Header = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [appId, setAppId] = useState('saleswit');
  
  // Gradient style matching MarketFit
  const headerGradient = {
    background: 'linear-gradient(to right, rgba(20, 32, 57, 0.9), rgba(30, 41, 59, 0.8))',
    backdropFilter: 'blur(4px)'
  };
  
  // Fetch user's subscription status
  useEffect(() => {
    if (user && user.google_uid) {
      fetchSubscription();
    }
  }, [user]);
  
  const fetchSubscription = async () => {
    try {
      setLoading(true);
      
      // Get JWT token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        setSubscription({ status: 'free', plan_name: 'Free' });
        return;
      }
      
      // Determine the current app based on your logic
      const path = window.location.pathname.toLowerCase();
      const currentAppId = path.includes('saleswit') ? 'saleswit' : 'saleswit';
      setAppId(currentAppId);
      
      const response = await fetch(`/api/subscriptions/user/${user.google_uid}?app_id=${currentAppId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSubscription(data.subscription);
      } else if (response.status === 404) {
        setSubscription({ status: 'free', plan_name: 'Free' });
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (err) {
      console.error('Error fetching subscription:', err);
      setSubscription({ status: 'free', plan_name: 'Free' });
    } finally {
      setLoading(false);
    }
  };

  const canUpgrade = () => {
    if (!subscription) return true;
    return subscription.plan_type?.toLowerCase() !== 'platinum';
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserDropdown && !event.target.closest('.profile-dropdown-container')) {
        setShowUserDropdown(false);
      }
      if (showMobileMenu && !event.target.closest('.mobile-menu-container')) {
        setShowMobileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserDropdown, showMobileMenu]);

  return (
    <>
      {/* Desktop Header */}
      <div className="bg-white">
        <header
          style={headerGradient}
          className="border-b border-gray-700 sticky top-0 z-30"
        >
          <div className="container mx-auto px-3 sm:px-4 lg:px-6 xl:px-8">
            <div className="flex justify-between items-center h-14 sm:h-16">
              {/* Logo and tagline */}
<div className="flex items-center min-w-0 flex-shrink">
  <Link to="/" className="flex-shrink-0 flex items-center">
    <img 
      src="https://mgimpacts.com/saleswit-white-logo.png" 
      alt="SalesWit Logo" 
      className="h-8 sm:h-10 lg:h-12 w-auto"
    />
    
  </Link>
  <a 
    href="https://mgimpacts.com" 
    className="hidden lg:flex items-baseline ml-2 cursor-pointer hover:opacity-80 transition-opacity duration-200"
    target="_blank"
    rel="noopener noreferrer"
  >
    <span className="text-white text-[0.6rem] relative -bottom-[1px]">
      by
    </span>
    <span className="text-white text-base ml-1">
      Mega
    </span>
    <span className="text-base ml-1 bg-gradient-to-r from-[#ff7e00] to-[#e11d48] bg-clip-text text-transparent">
      Impacts
    </span>
  </a>
</div>

              {/* Desktop Navigation */}
              <nav className="hidden lg:flex items-center space-x-2 xl:space-x-3">
                {isAuthenticated ? (
                  <>
                    <Link 
                      to="/create-cd" 
                      className="inline-flex bg-[#0c1526] items-center justify-center px-3 xl:px-4 py-1.5 xl:py-2 text-sm xl:text-base text-white rounded-full hover:shadow-[0_0_0_1px_#f59e0b] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-all duration-100"
                    >
                      <span className="hidden xl:inline">Create CD</span>
                      <span className="xl:hidden">Create</span>
                    </Link>
                    
                    <Link 
                      to="/view-cds" 
                      className="inline-flex bg-[#0c1526] items-center justify-center px-3 xl:px-4 py-1.5 xl:py-2 text-sm xl:text-base text-white rounded-full hover:shadow-[0_0_0_1px_#f59e0b] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-all duration-100"
                    >
                      <span className="hidden xl:inline">View CDs</span>
                      <span className="xl:hidden">View</span>
                    </Link>

                    <a
                      href="https://marketfit.mgimpacts.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex bg-[#0c1526] items-center justify-center px-3 xl:px-4 py-1.5 xl:py-2 text-sm xl:text-base text-white rounded-full hover:shadow-[0_0_0_1px_#f59e0b] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-all duration-100"
                    >
                      <span className="hidden xl:inline">MarketFit</span>
                      <span className="xl:hidden">Market</span>
                      <ArrowUpRight className="ml-1 xl:ml-1.5 h-3 w-3 xl:h-4 xl:w-4" />
                    </a>
                    
                    {/* Upgrade/Subscription Button */}
                    {user && canUpgrade() && (
                      <button
                        onClick={() => {
                          const isCancelled = subscription && (
                            (subscription.metadata?.cancellation_scheduled === true) ||
                            subscription.cancellation_scheduled === true ||
                            (subscription.metadata?.paypal_cancelled === true)
                          );
                          
                          if (isCancelled) {
                            alert("Your subscription is scheduled for cancellation. Please contact support.");
                            return;
                          }
                          setShowSubscriptionModal(true);
                        }}
                        className="inline-flex bg-[#0c1526] items-center justify-center px-3 xl:px-4 py-1.5 xl:py-2 text-sm xl:text-base text-white rounded-full hover:shadow-[0_0_0_1px_#f59e0b] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-all duration-100"
                      >
                        <Crown className="mr-1 xl:mr-2 h-3 w-3 xl:h-4 xl:w-4" />
                        <span className="hidden xl:inline">Upgrade</span>
                        <span className="xl:hidden">Upgrade</span>
                      </button>
                    )}
                    
                    {/* Enhanced User Profile Menu */}
                    <div className="profile-dropdown-container relative px-2 py-1.5 bg-[#0c1526] rounded-full hover:shadow-[0_0_0_1px_#f59e0b]">
                      <button
                        className="flex items-center text-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowUserDropdown(!showUserDropdown);
                        }}
                        title={`Logged in as ${user?.name || user?.email}`}
                      >
                        <User className="h-4 w-4 xl:h-5 xl:w-4 my-1 mx-1 text-gray-100 hover:text-[#ff7e00]" />
                      </button>
                      
                      {/* User dropdown menu */}
                      {showUserDropdown && (
                        <div className="absolute right-0 mt-2 w-48 xl:w-56 rounded-md shadow-xl bg-[#1e293b] ring-1 ring-gray-700 focus:outline-none z-50">
                          <div className="py-1" role="none">
                            
                            <Link
                              to="/profile-settings"
                              className="group flex items-center px-3 xl:px-4 py-2 xl:py-3 text-sm text-gray-200 hover:bg-[#273349]"
                              role="menuitem"
                              onClick={() => setShowUserDropdown(false)}
                            >
                              <Settings className="mr-2 xl:mr-3 h-4 w-4 xl:h-5 xl:w-5 text-gray-400 group-hover:text-orange-300" />
                              Profile
                            </Link>
                            
                            <Link
                              to="/subscription-dashboard"
                              className="group flex items-center px-3 xl:px-4 py-2 xl:py-3 text-sm text-gray-200 hover:bg-[#273349]"
                              role="menuitem"
                              onClick={() => setShowUserDropdown(false)}
                            >
                              <CreditCard className="mr-2 xl:mr-3 h-4 w-4 xl:h-5 xl:w-5 text-gray-400 group-hover:text-orange-300" />
                              Subscription
                            </Link>
                            
                            <div className="border-t border-gray-600 my-1"></div>
                            
                            <button
                              className="w-full text-left group flex items-center px-3 xl:px-4 py-2 xl:py-3 text-sm text-gray-200 hover:bg-[#273349]"
                              role="menuitem"
                              onClick={() => {
                                setShowUserDropdown(false);
                                logout();
                              }}
                            >
                              <LogOut className="mr-2 xl:mr-3 h-4 w-4 xl:h-5 xl:w-5 text-gray-400 group-hover:text-orange-300" />
                              Log Out
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="flex items-center space-x-2 xl:space-x-3">
                    <Link 
                      to="/login" 
                      className="px-3 xl:px-4 py-1.5 xl:py-2 text-sm xl:text-base font-medium text-gray-200 hover:text-white"
                    >
                      Log In
                    </Link>
                    <Link 
                      to="/signup" 
                      className="px-3 xl:px-4 py-1.5 xl:py-2 bg-gradient-to-r from-[#ff7e00] to-[#e11d48] text-white rounded-md text-sm xl:text-base font-medium hover:shadow-lg transition-shadow"
                    >
                      Sign Up
                    </Link>
                  </div>
                )}
              </nav>

              {/* Mobile/Tablet Menu Button */}
              <div className="lg:hidden mobile-menu-container relative">
                <button
                  className="inline-flex items-center justify-center p-2 rounded-md text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                >
                  {showMobileMenu ? (
                    <X className="h-6 w-6" />
                  ) : (
                    <Menu className="h-6 w-6" />
                  )}
                </button>

                {/* Mobile/Tablet Menu Dropdown */}
                {showMobileMenu && (
                  <div className="absolute right-0 mt-2 w-64 rounded-md shadow-xl bg-[#1e293b] ring-1 ring-gray-700 focus:outline-none z-50">
                    <div className="py-2" role="none">
                      {isAuthenticated ? (
                        <>
                          <div className="px-4 py-2 border-b border-gray-600">
                            <p className="text-white font-medium text-sm">{user?.name || 'User'}</p>
                            <p className="text-gray-300 text-xs">{user?.email}</p>
                          </div>

                          <Link
                            to="/create-cd"
                            className="flex items-center px-4 py-3 text-sm text-gray-200 hover:bg-[#273349]"
                            onClick={() => setShowMobileMenu(false)}
                          >
                            <Layers className="mr-3 h-5 w-5 text-gray-400" />
                            Create CD
                          </Link>

                          <Link
                            to="/view-cds"
                            className="flex items-center px-4 py-3 text-sm text-gray-200 hover:bg-[#273349]"
                            onClick={() => setShowMobileMenu(false)}
                          >
                            <Clock className="mr-3 h-5 w-5 text-gray-400" />
                            View CDs
                          </Link>

                          <a
                            href="https://marketfit.mgimpacts.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center px-4 py-3 text-sm text-gray-200 hover:bg-[#273349]"
                            onClick={() => setShowMobileMenu(false)}
                          >
                            <ArrowUpRight className="mr-3 h-5 w-5 text-gray-400" />
                            MarketFit
                          </a>

                          {user && canUpgrade() && (
                            <button
                              onClick={() => {
                                const isCancelled = subscription && (
                                  (subscription.metadata?.cancellation_scheduled === true) ||
                                  subscription.cancellation_scheduled === true ||
                                  (subscription.metadata?.paypal_cancelled === true)
                                );
                                
                                if (isCancelled) {
                                  alert("Your subscription is scheduled for cancellation. Please contact support.");
                                  return;
                                }
                                setShowMobileMenu(false);
                                setShowSubscriptionModal(true);
                              }}
                              className="w-full text-left flex items-center px-4 py-3 text-sm text-gray-200 hover:bg-[#273349]"
                            >
                              <Crown className="mr-3 h-5 w-5 text-gray-400" />
                              Upgrade
                            </button>
                          )}

                          <div className="border-t border-gray-600 my-1"></div>

                          <Link
                            to="/profile-settings"
                            className="flex items-center px-4 py-3 text-sm text-gray-200 hover:bg-[#273349]"
                            onClick={() => setShowMobileMenu(false)}
                          >
                            <Settings className="mr-3 h-5 w-5 text-gray-400" />
                            Profile Settings
                          </Link>

                          <Link
                            to="/subscription-dashboard"
                            className="flex items-center px-4 py-3 text-sm text-gray-200 hover:bg-[#273349]"
                            onClick={() => setShowMobileMenu(false)}
                          >
                            <CreditCard className="mr-3 h-5 w-5 text-gray-400" />
                            Subscription
                          </Link>

                          <button
                            className="w-full text-left flex items-center px-4 py-3 text-sm text-gray-200 hover:bg-[#273349]"
                            onClick={() => {
                              setShowMobileMenu(false);
                              logout();
                            }}
                          >
                            <LogOut className="mr-3 h-5 w-5 text-gray-400" />
                            Log Out
                          </button>
                        </>
                      ) : (
                        <>
                          <Link
                            to="/login"
                            className="flex items-center px-4 py-3 text-sm text-gray-200 hover:bg-[#273349]"
                            onClick={() => setShowMobileMenu(false)}
                          >
                            <LogIn className="mr-3 h-5 w-5 text-gray-400" />
                            Log In
                          </Link>
                          <Link
                            to="/signup"
                            className="flex items-center px-4 py-3 text-sm text-gray-200 hover:bg-[#273349]"
                            onClick={() => setShowMobileMenu(false)}
                          >
                            <UserPlus className="mr-3 h-5 w-5 text-gray-400" />
                            Sign Up
                          </Link>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>
      </div>

      {/* Mobile Bottom Navigation - only show if authenticated and on very small screens */}
      {isAuthenticated && <MobileBottomNav 
        user={user} 
        appId={appId}
        subscription={subscription}
        onShowSubscriptionModal={() => setShowSubscriptionModal(true)}
        onLogout={logout}
        canUpgrade={canUpgrade}
      />}
      
      {/* Subscription Modal */}
      {user && (
        <SubscriptionModal
          isOpen={showSubscriptionModal}
          onClose={() => setShowSubscriptionModal(false)}
          user={user}
          currentPlan={subscription}
          appId={appId}
        />
      )}
    </>
  );
};

const MobileBottomNav = ({ user, appId, subscription, onShowSubscriptionModal, onLogout, canUpgrade }) => {
  const location = useLocation();
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Gradient style matching MarketFit
  const headerGradient = {
    background: 'linear-gradient(to right, rgba(20, 32, 57, 0.9), rgba(30, 41, 59, 0.8))',
    backdropFilter: 'blur(4px)'
  };

  return (
    <>
      {/* Mobile Bottom Navigation - only show on small screens */}
      <div 
        className="sm:hidden fixed bottom-0 left-0 right-0 border-t border-gray-700 z-40"
        style={headerGradient}
      >
        <div className="flex justify-around items-center py-2">
          <Link
            to="/create-cd"
            className="flex flex-col items-center justify-center p-2 text-white rounded-full hover:bg-white/10 transition-colors"
          >
            <Layers className="h-5 w-5" />
            <span className="text-xs mt-1">Create</span>
          </Link>

          <Link
            to="/view-cds"
            className="flex flex-col items-center justify-center p-2 text-white rounded-full hover:bg-white/10 transition-colors"
          >
            <Clock className="h-5 w-5" />
            <span className="text-xs mt-1">View</span>
          </Link>

          <a
            href="https://marketfit.mgimpacts.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center justify-center p-2 text-white rounded-full hover:bg-white/10 transition-colors"
          >
            <ArrowUpRight className="h-5 w-5" />
            <span className="text-xs mt-1">Market</span>
          </a>

          {user && canUpgrade() && (
            <button
              onClick={() => {
                const isCancelled = subscription && (
                  (subscription.metadata?.cancellation_scheduled === true) ||
                  subscription.cancellation_scheduled === true ||
                  (subscription.metadata?.paypal_cancelled === true)
                );
                
                if (isCancelled) {
                  alert("Your subscription is scheduled for cancellation. Please contact support.");
                  return;
                }
                onShowSubscriptionModal();
              }}
              className="flex flex-col items-center justify-center p-2 text-white rounded-full hover:bg-white/10 transition-colors"
            >
              <Crown className="h-5 w-5" />
              <span className="text-xs mt-1">Upgrade</span>
            </button>
          )}

          <div className="profile-dropdown-container relative">
            <button
              className="flex flex-col items-center justify-center p-2 text-white rounded-full hover:bg-white/10 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                setShowMobileMenu(!showMobileMenu);
              }}
            >
              <User className="h-5 w-5" />
              <span className="text-xs mt-1">Profile</span>
            </button>

            {/* Mobile More Menu Modal */}
            {showMobileMenu && (
              <div className="fixed inset-0 bg-black/50 z-60" onClick={() => setShowMobileMenu(false)}>
                <div 
                  className="fixed bottom-0 left-0 right-0 rounded-t-lg border-t border-gray-700"
                  style={headerGradient}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="p-4 space-y-3">
                    <div className="text-center border-b border-white/20 pb-3">
                      <p className="text-white font-medium">{user?.name || 'User'}</p>
                      <p className="text-gray-300 text-sm">{user?.email}</p>
                    </div>
                    
                    <Link
                      to="/profile-settings"
                      className="flex items-center space-x-3 text-white hover:text-orange-300 p-2 rounded"
                      onClick={() => setShowMobileMenu(false)}
                    >
                      <Settings className="w-5 h-5" />
                      <span>Profile Settings</span>
                    </Link>
                    
                    <Link
                      to="/subscription-dashboard"
                      className="flex items-center space-x-3 text-white hover:text-orange-300 p-2 rounded"
                      onClick={() => setShowMobileMenu(false)}
                    >
                      <CreditCard className="w-5 h-5" />
                      <span>Billing Dashboard</span>
                    </Link>
                    
                    <button
                      onClick={() => {
                        setShowMobileMenu(false);
                        onLogout();
                      }}
                      className="flex items-center space-x-3 text-white hover:text-orange-300 p-2 rounded w-full text-left"
                    >
                      <LogOut className="w-5 h-5" />
                      <span>Log Out</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Show login/signup for non-authenticated users */}
          {!user && (
            <>
              <Link
                to="/login"
                className="flex flex-col items-center justify-center p-2 text-white rounded-full hover:bg-white/10 transition-colors"
              >
                <LogIn className="h-5 w-5" />
                <span className="text-xs mt-1">Log In</span>
              </Link>
              <Link
                to="/signup"
                className="flex flex-col items-center justify-center p-2 text-white rounded-full hover:bg-white/10 transition-colors"
              >
                <UserPlus className="h-5 w-5" />
                <span className="text-xs mt-1">Sign Up</span>
              </Link>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Header;